import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { Calendar, Users, BookOpen, Radio, Trophy, UserCheck, GitCompare } from 'lucide-react';

const RECORD_LINKS = [
  { role: 'PARTICIPANT', label: 'Playing Record', to: '/profile?tab=participant' },
  { role: 'ORGANIZER',  label: 'Organising Record', to: '/profile?tab=organizer' },
  { role: 'SPONSOR',    label: 'Sponsorship Record', to: '/profile?tab=sponsor' },
  { role: 'COACH',      label: 'Coach Dashboard', to: '/coach/dashboard' },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [recordMenuOpen, setRecordMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { sportFilter } = useConfig();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const navLinks = sportFilter
    ? [
        ...(isAuthenticated ? [{ to: '/teams',   label: 'My Teams', icon: <Users       className="w-4 h-4" /> }] : []),
        ...(isAuthenticated ? [{ to: '/diary',   label: 'My Diary', icon: <BookOpen    className="w-4 h-4" /> }] : []),
        { to: '/formation-compare', label: 'Compare Lineups', icon: <GitCompare className="w-4 h-4" /> },
        { to: '/live',    label: 'Live',    icon: <Radio      className="w-4 h-4" /> },
        { to: '/coaches', label: 'Coaches', icon: <UserCheck  className="w-4 h-4" /> },
      ]
    : [
        { to: '/events',  label: 'Events',   icon: <Calendar   className="w-4 h-4" /> },
        ...(isAuthenticated ? [{ to: '/teams',  label: 'My Teams', icon: <Users      className="w-4 h-4" /> }] : []),
        ...(isAuthenticated ? [{ to: '/diary',  label: 'My Diary', icon: <BookOpen   className="w-4 h-4" /> }] : []),
        { to: '/live',    label: 'Live',     icon: <Radio      className="w-4 h-4" /> },
        { to: '/sports',  label: 'Sports',   icon: <Trophy     className="w-4 h-4" /> },
        { to: '/coaches', label: 'Coaches',  icon: <UserCheck  className="w-4 h-4" /> },
      ];

  const userRecordLinks = useMemo(() => {
    const roles = user?.roles || [];
    return RECORD_LINKS.filter((link) => roles.includes(link.role));
  }, [user?.roles]);

  const toggleRecordMenu = () => {
    setRecordMenuOpen((prev) => !prev);
    setUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen((prev) => !prev);
    setRecordMenuOpen(false);
  };

  const navLinkClass = "px-3 py-2 text-sm font-medium text-gray-400 hover:text-white rounded-md hover:bg-white/5 transition-all duration-150 whitespace-nowrap tracking-wide";
  const dropdownItemClass = "block px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors";

  return (
    <nav className="bg-gray-950/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">

          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="KridaClub" className="h-8 w-8 rounded-lg" />
              <span className="text-xl font-display font-normal gradient-text tracking-tight">KridaClub</span>
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={navLinkClass}>
                <span className="flex items-center gap-1.5">
                  {link.icon}
                  {link.label}
                </span>
              </Link>
            ))}

            {/* My Record dropdown */}
            {isAuthenticated && (
              <div className="relative">
                <button
                  onClick={toggleRecordMenu}
                  className={`flex items-center gap-1 ${navLinkClass}`}
                >
                  My Record
                  <svg
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${recordMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {recordMenuOpen && (
                  <>
                    <div className="fixed inset-0" onClick={() => setRecordMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-52 bg-gray-900 rounded-xl shadow-2xl shadow-black/40 border border-white/8 py-1 animate-slide-down">
                      {userRecordLinks.length > 0 ? (
                        userRecordLinks.map((link) => (
                          <Link
                            key={link.role}
                            to={link.to}
                            onClick={() => setRecordMenuOpen(false)}
                            className={dropdownItemClass}
                          >
                            {link.label}
                          </Link>
                        ))
                      ) : (
                        <Link
                          to="/profile"
                          onClick={() => setRecordMenuOpen(false)}
                          className={dropdownItemClass}
                        >
                          View Profile
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Desktop auth */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                >
                  <div className="h-7 w-7 rounded-full bg-primary-900/70 border border-primary-700/60 flex items-center justify-center">
                    <span className="text-primary-300 font-semibold text-xs uppercase">
                      {user?.firstName?.[0] || 'U'}
                    </span>
                  </div>
                  <span className="text-gray-300">{user?.firstName || 'Account'}</span>
                  <svg className="h-3.5 w-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-xl shadow-2xl shadow-black/40 border border-white/8 py-1 animate-slide-down">
                      <Link to="/dashboard" onClick={() => setUserMenuOpen(false)} className={dropdownItemClass}>Dashboard</Link>
                      <Link to="/profile"   onClick={() => setUserMenuOpen(false)} className={dropdownItemClass}>Profile</Link>
                      <Link to="/bookmarks" onClick={() => setUserMenuOpen(false)} className={dropdownItemClass}>Saved Posts</Link>
                      <hr className="my-1 border-white/8" />
                      <button onClick={handleLogout} className="block w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login"  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-white/10 rounded-lg hover:border-white/20 hover:bg-white/5 transition-all duration-200">Sign In</Link>
                <Link to="/signup" className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500 transition-all duration-200">Get Started</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              {mobileMenuOpen ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-white/5 animate-slide-down bg-gray-950/95 backdrop-blur-xl">
          <div className="px-4 py-3 space-y-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5 tracking-wide"
              >
                {link.icon && <span className="text-gray-500">{link.icon}</span>}
                {link.label}
              </Link>
            ))}

            {/* My Record — mobile */}
            {isAuthenticated && userRecordLinks.length > 0 && (
              <>
                <div className="px-3 pt-3 pb-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">My Record</span>
                </div>
                {userRecordLinks.map((link) => (
                  <Link
                    key={link.role}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5"
                  >
                    {link.label}
                  </Link>
                ))}
              </>
            )}

            <hr className="my-2 border-white/8" />

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5">Dashboard</Link>
                <Link to="/profile"   onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5">Profile</Link>
                <Link to="/bookmarks" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5">Saved Posts</Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-3 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-2 pb-1">
                <Link to="/login"  onClick={() => setMobileMenuOpen(false)} className="px-4 py-2.5 text-sm font-medium text-center text-gray-200 border border-white/10 rounded-lg hover:bg-white/5 transition-colors">Sign In</Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2.5 text-sm font-medium text-center text-white bg-primary-600 rounded-lg hover:bg-primary-500 transition-colors">Get Started</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
